import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

import { useServiceError } from '@/hooks';

import { ListNcmsQuery, Ncm, UpdateNcmRequest } from './Ncm.types';
import {
  createNcm,
  deleteNcm,
  getNcm,
  listNcms,
  ncmActivateOrInactivate,
  reduceNcm,
  updateNcm,
} from './Ncm.service';

import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import { useIntl } from 'react-intl';
import { message, notification } from 'antd';
import Authorities from '@/types/Authority';
import notificationError from '@/utils/functions/notificationError';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

export function useNcms(params?: ListNcmsQuery) {
  const query = useInfiniteQuery(
    ['ncms', { ...params }],
    ({ pageParam }) => {
      const req: ListNcmsQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listNcms(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      enabled: hasPermissions([Authorities.REGISTER_NCM_VIEW]),
    },
  );

  useServiceError(query.error);

  return query;
}

export function useNcmsMutation() {
  return useMutation(
    (params?: ListNcmsQuery) => {
      const req: ListNcmsQuery = {
        order: params?.order || DEFAULT_ORDER,
        ...params,
      };

      return listNcms(req);
    },
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useNcm(id?: number) {
  const query = useQuery(['ncm', { id }], () => (id ? getNcm(id) : undefined), {
    enabled: !!id && hasPermissions([Authorities.REGISTER_NCM_VIEW]),
  });

  useServiceError(query.error);

  return query;
}

export function useNcmMutation() {
  const queryClient = useQueryClient();

  const mutation = useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<Ncm>(['ncm-detail-mutation', id]);

      if (cache?.id === id) {
        return cache;
      }

      const data = await getNcm(id);

      return data;
    },
    {
      onSuccess(data, id) {
        queryClient.setQueryData(['ncm-detail-mutation', id], data);
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useCreateNcm() {
  const intl = useIntl();
  const mutation = useMutation(createNcm, {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'ncm.create.notification.success',
        }),
        description: intl.formatMessage({
          id: 'ncm.create.notification.successDescription',
        }),
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useUpdateNcm() {
  const intl = useIntl();
  const queryChanel = useQueryChanel();

  const mutation = useMutation((obj: UpdateNcmRequest) => updateNcm(obj), {
    onSuccess(_, data) {
      notification.success({
        message: intl.formatMessage({
          id: 'ncm.update.notification.success',
        }),
        description: intl.formatMessage({
          id: 'ncm.update.notification.successDescription',
        }),
      });

      queryChanel({
        invalidateQueries: [['ncm-detail-mutation', data.id], ['ncms']],
        resetQueries: [['ncm-detail-mutation', data.id], ['ncms']],
      });
    },
  });

  useServiceError(mutation.error);

  return mutation;
}

export function useDeleteNcm() {
  const queryClient = useQueryClient();

  const intl = useIntl();
  const mutation = useMutation((id: number) => deleteNcm(id), {
    onSuccess() {
      notification.success({
        message: intl.formatMessage({
          id: 'ncm.remove.notification.success',
        }),
        description: intl.formatMessage({
          id: 'ncm.remove.notification.successDescription',
        }),
      });
      queryClient.invalidateQueries(['ncms']);
    },
    onError(error) {
      notificationError(error, {
        translate: (errors) =>
          errors.map((err) =>
            err.includes('NCM Already linked to part/consumable.')
              ? 'Não é possível excluir NCM, pois possui vinculos com outros módulos.'
              : err.includes('Ncm already has a link with another entity:')
              ? 'Não é possível excluir o NCM, pois possui vinculos com outros módulos.'
              : err,
          ),
      });
    },
  });

  return mutation;
}

export function useActiveOrInactivateNcm() {
  const intl = useIntl();

  const mutation = useMutation(
    (ncmId: number) => ncmActivateOrInactivate(ncmId),
    {
      onSuccess() {
        message.success(
          intl.formatMessage({ id: 'ncm.status.message.success' }),
        );
      },
    },
  );

  useServiceError(mutation.error);

  return mutation;
}

export function useReducedNcm(
  params: Omit<ListNcmsQuery, 'active'>,
  enabled = true,
) {
  const queryClient = useQueryClient();
  const cache = queryClient.getQueryData(['ncms', { ...params }]);

  const query = useInfiniteQuery(
    ['ncms', { ...params }],
    ({ pageParam }) => {
      const req: Omit<ListNcmsQuery, 'active'> = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };
      return reduceNcm(req);
    },
    {
      getNextPageParam: (lastPage) => lastPage.nextToken,
      cacheTime: 10000,
      enabled: enabled && !cache,
    },
  );
  return query;
}
